import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Database, Table, Search, Trash2, Plus, Edit, RefreshCw,
  AlertTriangle, ChevronLeft, ChevronRight, Download, Code, X, Save, Shield, FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function DatabaseAdminPage() {
  const { api } = useAuth();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableSchema, setTableSchema] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingRow, setEditingRow] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRowData, setNewRowData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [showSQLQuery, setShowSQLQuery] = useState(false);
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlResults, setSqlResults] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Load all tables on mount
  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/database/tables/');
      setTables(res.data.tables);
    } catch (error) {
      toast.error('Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  const selectTable = async (table) => {
    setSelectedTable(table);
    setPage(1);
    setSearch('');
    loadTableSchema(table);
    loadTableData(table, 1, '');
  };

  const loadTableSchema = async (table) => {
    try {
      const res = await api.get(`/admin/database/${table.app_label}/${table.model_name}/schema/`);
      setTableSchema(res.data);
    } catch (error) {
      toast.error('Failed to load table schema');
    }
  };

  const loadTableData = async (table, pageNum = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/database/${table.app_label}/${table.model_name}/data/`, {
        params: { page: pageNum, page_size: 50, search: searchTerm }
      });
      setTableData(res.data.rows);
      setTotalPages(res.data.total_pages);
      setPage(pageNum);
    } catch (error) {
      toast.error('Failed to load table data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (selectedTable) {
      loadTableData(selectedTable, 1, search);
    }
  };

  const checkDeleteImpact = async (rowId) => {
    try {
      const res = await api.get(`/admin/database/${selectedTable.app_label}/${selectedTable.model_name}/${rowId}/check-delete/`);
      return res.data;
    } catch (error) {
      toast.error('Failed to check delete impact');
      return null;
    }
  };

  const handleDeleteRow = async (rowId) => {
    // First check the impact
    const impact = await checkDeleteImpact(rowId);
    if (!impact) return;

    if (impact.has_warnings) {
      // Show warning modal
      setDeleteWarning(impact);
      setPendingDeleteId(rowId);
      setShowDeleteWarning(true);
    } else {
      // No dependencies, proceed with simple confirmation
      if (!confirm('Are you sure you want to delete this row?')) return;
      await executeDelete(rowId);
    }
  };

  const executeDelete = async (rowId) => {
    try {
      await api.delete(`/admin/database/${selectedTable.app_label}/${selectedTable.model_name}/${rowId}/delete/`);
      toast.success('Row deleted successfully');
      loadTableData(selectedTable, page, search);
      loadTables(); // Refresh counts
      setShowDeleteWarning(false);
      setPendingDeleteId(null);
      setDeleteWarning(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete row');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('No rows selected');
      return;
    }

    // For bulk delete, check impact of first row as example
    const impact = await checkDeleteImpact(selectedRows[0]);
    if (!impact) return;

    if (impact.has_warnings) {
      // Show warning with bulk context
      const bulkWarning = {
        ...impact,
        bulk_mode: true,
        bulk_count: selectedRows.length
      };
      setDeleteWarning(bulkWarning);
      setPendingDeleteId(selectedRows);
      setShowDeleteWarning(true);
    } else {
      // No dependencies, proceed with simple confirmation
      if (!confirm(`⚠️ Delete ${selectedRows.length} selected row(s)? This cannot be undone.`)) return;
      await executeBulkDelete();
    }
  };

  const executeBulkDelete = async () => {
    const rowsToDelete = Array.isArray(pendingDeleteId) ? pendingDeleteId : selectedRows;
    try {
      await Promise.all(
        rowsToDelete.map(rowId =>
          api.delete(`/admin/database/${selectedTable.app_label}/${selectedTable.model_name}/${rowId}/delete/`)
        )
      );
      toast.success(`Deleted ${rowsToDelete.length} rows successfully`);
      setSelectedRows([]);
      loadTableData(selectedTable, page, search);
      loadTables();
      setShowDeleteWarning(false);
      setPendingDeleteId(null);
      setDeleteWarning(null);
    } catch (error) {
      toast.error('Failed to delete some rows');
    }
  };

  const handleAddRow = async () => {
    try {
      await api.post(`/admin/database/${selectedTable.app_label}/${selectedTable.model_name}/create/`, newRowData);
      toast.success('Row created successfully');
      setShowAddRow(false);
      setNewRowData({});
      loadTableData(selectedTable, page, search);
      loadTables();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create row');
    }
  };

  const handleUpdateRow = async (rowId) => {
    try {
      await api.put(`/admin/database/${selectedTable.app_label}/${selectedTable.model_name}/${rowId}/update/`, editingRow);
      toast.success('Row updated successfully');
      setEditingRow(null);
      loadTableData(selectedTable, page, search);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update row');
    }
  };

  const handleExportCSV = () => {
    if (!tableData.length) {
      toast.error('No data to export');
      return;
    }

    const fields = tableSchema?.fields || [];
    const headers = fields.map(f => f.verbose_name).join(',');
    const rows = tableData.map(row => 
      fields.map(f => {
        const value = row[f.name];
        if (value === null || value === undefined) return '';
        // Escape commas and quotes
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
      }).join(',')
    ).join('\n');

    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTable.model_name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const handleExecuteSQL = async () => {
    if (!sqlQuery.trim()) {
      toast.error('Please enter a SQL query');
      return;
    }

    try {
      // For now, show a message - you can implement actual SQL execution on backend
      toast.info('SQL Query execution is available on backend. Contact admin for direct database queries.');
      setSqlResults({ 
        message: 'For safety, direct SQL execution is restricted. Use the table management features above.',
        query: sqlQuery 
      });
    } catch (error) {
      toast.error('Failed to execute query');
    }
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRows(prev => 
      prev.includes(rowId) 
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === tableData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map(row => getRowId(row)));
    }
  };

  const handleTruncateTable = async () => {
    if (!confirm(`⚠️ WARNING: This will delete ALL ${selectedTable.row_count} rows from ${selectedTable.verbose_name}. This action cannot be undone. Are you sure?`)) return;
    
    const confirmText = prompt(`Type "${selectedTable.model_name}" to confirm:`);
    if (confirmText !== selectedTable.model_name) {
      toast.error('Confirmation failed');
      return;
    }

    try {
      const res = await api.delete(`/admin/database/${selectedTable.app_label}/${selectedTable.model_name}/truncate/`);
      toast.success(`Deleted ${res.data.deleted_count} rows`);
      loadTableData(selectedTable, 1, '');
      loadTables(); // Refresh counts
    } catch (error) {
      toast.error('Failed to truncate table');
    }
  };

  const getPrimaryKeyField = () => {
    return tableSchema?.fields.find(f => f.primary_key);
  };

  const getRowId = (row) => {
    const pkField = getPrimaryKeyField();
    return pkField ? row[pkField.name] : row.id;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3 text-gray-900">
            <Database className="w-8 h-8 text-blue-600" />
            Database Admin Panel
          </h1>
          <p className="text-gray-600 mt-2">Manage all database tables, view, edit, and delete data</p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Tables List */}
          <div className="col-span-12 lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Table className="w-5 h-5" />
                  Tables ({tables.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[700px] overflow-y-auto">
                  {tables.map(table => (
                    <button
                      key={`${table.app_label}.${table.model_name}`}
                      onClick={() => selectTable(table)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedTable?.model_name === table.model_name
                          ? 'bg-blue-100 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="font-medium text-sm">{table.verbose_name}</div>
                      <div className="text-xs text-gray-500">{table.app_label}.{table.model_name}</div>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {table.row_count.toLocaleString()} rows
                      </Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Table Data */}
          <div className="col-span-12 lg:col-span-9">
            {!selectedTable ? (
              <Card className="h-[500px] flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Select a table to view and manage data</p>
                </div>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {selectedTable.verbose_name}
                      <Badge>{selectedTable.row_count.toLocaleString()} rows</Badge>
                      {selectedRows.length > 0 && (
                        <Badge variant="secondary">{selectedRows.length} selected</Badge>
                      )}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddRow(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Row
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportCSV}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                      {selectedRows.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBulkDelete}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete ({selectedRows.length})
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSQLQuery(!showSQLQuery)}
                      >
                        <Code className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadTableData(selectedTable, page, search)}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleTruncateTable}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search */}
                  <div className="flex gap-2 mt-4">
                    <Input
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleSearch}>
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>

                  {/* SQL Query Builder */}
                  {showSQLQuery && (
                    <div className="mt-4 p-4 bg-gray-900 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-white font-mono text-sm">SQL Query</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowSQLQuery(false)}
                        >
                          <X className="w-4 h-4 text-white" />
                        </Button>
                      </div>
                      <Textarea
                        value={sqlQuery}
                        onChange={(e) => setSqlQuery(e.target.value)}
                        placeholder="SELECT * FROM table_name WHERE ..."
                        className="font-mono text-sm bg-gray-800 text-white border-gray-700 mb-2"
                        rows={4}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleExecuteSQL}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Execute Query
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSqlQuery(`SELECT * FROM ${selectedTable.table_name} LIMIT 100;`)}
                        >
                          Sample Query
                        </Button>
                      </div>
                      {sqlResults && (
                        <div className="mt-2 p-2 bg-gray-800 rounded text-white text-sm">
                          {sqlResults.message || JSON.stringify(sqlResults, null, 2)}
                        </div>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-12">Loading...</div>
                  ) : tableData.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No data found
                    </div>
                  ) : (
                    <>
                      {/* Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-2 w-10">
                                <Checkbox
                                  checked={selectedRows.length === tableData.length && tableData.length > 0}
                                  onCheckedChange={toggleSelectAll}
                                />
                              </th>
                              {tableSchema?.fields.map(field => (
                                <th key={field.name} className="text-left p-2 font-medium">
                                  {field.verbose_name}
                                  <span className="text-xs text-gray-400 ml-1">({field.type})</span>
                                </th>
                              ))}
                              <th className="text-right p-2 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableData.map((row, idx) => {
                              const rowId = getRowId(row);
                              const isEditing = editingRow && getRowId(editingRow) === rowId;
                              
                              return (
                                <tr key={idx} className="border-b hover:bg-gray-50">
                                  <td className="p-2">
                                    <Checkbox
                                      checked={selectedRows.includes(rowId)}
                                      onCheckedChange={() => toggleRowSelection(rowId)}
                                    />
                                  </td>
                                  {tableSchema?.fields.map(field => (
                                    <td key={field.name} className="p-2 max-w-xs">
                                      {isEditing ? (
                                        <Input
                                          value={editingRow[field.name] || ''}
                                          onChange={(e) => setEditingRow({...editingRow, [field.name]: e.target.value})}
                                          className="h-8 text-xs"
                                        />
                                      ) : (
                                        <span className="truncate block">
                                          {row[field.name] !== null && row[field.name] !== undefined 
                                            ? String(row[field.name]).substring(0, 100)
                                            : <span className="text-gray-400 italic">null</span>
                                          }
                                        </span>
                                      )}
                                    </td>
                                  ))}
                                  <td className="p-2 text-right">
                                    <div className="flex justify-end gap-1">
                                      {isEditing ? (
                                        <>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleUpdateRow(rowId)}
                                          >
                                            <Save className="w-4 h-4 text-green-600" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditingRow(null)}
                                          >
                                            <X className="w-4 h-4 text-gray-600" />
                                          </Button>
                                        </>
                                      ) : (
                                        <>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditingRow({...row})}
                                          >
                                            <Edit className="w-4 h-4 text-blue-600" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteRow(rowId)}
                                          >
                                            <Trash2 className="w-4 h-4 text-red-600" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-600">
                          Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => loadTableData(selectedTable, page - 1, search)}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page === totalPages}
                            onClick={() => loadTableData(selectedTable, page + 1, search)}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Add New Row Modal */}
        <Dialog open={showAddRow} onOpenChange={setShowAddRow}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Row - {selectedTable?.verbose_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {tableSchema?.fields
                .filter(f => !f.primary_key) // Exclude primary key (usually auto-generated)
                .map(field => (
                  <div key={field.name} className="space-y-2">
                    <Label>
                      {field.verbose_name}
                      {!field.null && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <div className="text-xs text-gray-500">
                      Type: {field.type}
                      {field.max_length && ` (max: ${field.max_length})`}
                      {field.choices && ` - Choices: ${field.choices.map(c => c[0]).join(', ')}`}
                    </div>
                    {field.choices ? (
                      <select
                        value={newRowData[field.name] || ''}
                        onChange={(e) => setNewRowData({...newRowData, [field.name]: e.target.value})}
                        className="w-full border rounded p-2"
                      >
                        <option value="">-- Select --</option>
                        {field.choices.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    ) : field.type === 'TextField' ? (
                      <Textarea
                        value={newRowData[field.name] || ''}
                        onChange={(e) => setNewRowData({...newRowData, [field.name]: e.target.value})}
                        placeholder={`Enter ${field.verbose_name}`}
                      />
                    ) : field.type === 'BooleanField' ? (
                      <Checkbox
                        checked={newRowData[field.name] || false}
                        onCheckedChange={(checked) => setNewRowData({...newRowData, [field.name]: checked})}
                      />
                    ) : (
                      <Input
                        type={field.type === 'IntegerField' || field.type === 'DecimalField' ? 'number' : 
                              field.type === 'DateField' ? 'date' : 
                              field.type === 'DateTimeField' ? 'datetime-local' : 'text'}
                        value={newRowData[field.name] || ''}
                        onChange={(e) => setNewRowData({...newRowData, [field.name]: e.target.value})}
                        placeholder={`Enter ${field.verbose_name}`}
                        maxLength={field.max_length}
                      />
                    )}
                  </div>
                ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAddRow(false);
                setNewRowData({});
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddRow}>
                <Plus className="w-4 h-4 mr-2" />
                Add Row
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Warning Modal */}
        <Dialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="delete-warning-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-6 h-6" />
                {deleteWarning?.bulk_mode 
                  ? `Delete Impact Warning (${deleteWarning.bulk_count} rows)`
                  : 'Delete Impact Warning'
                }
              </DialogTitle>
            </DialogHeader>
            
            {deleteWarning && (
              <div id="delete-warning-description" className="space-y-6 py-4">
                {/* Summary */}
                <div className={`p-4 rounded-lg border-2 ${
                  deleteWarning.can_delete ? 'bg-yellow-50 border-yellow-400' : 'bg-red-50 border-red-400'
                }`}>
                  <div className="flex items-start gap-3">
                    {deleteWarning.can_delete ? (
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    ) : (
                      <Shield className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-2">Impact Summary</h3>
                      <div className="space-y-1 text-sm">
                        {deleteWarning.summary.will_cascade > 0 && (
                          <p className="text-red-700">
                            ⚠️ <strong>{deleteWarning.summary.will_cascade}</strong> related record(s) will be <strong>permanently deleted</strong> (CASCADE)
                          </p>
                        )}
                        {deleteWarning.summary.will_set_null > 0 && (
                          <p className="text-yellow-700">
                            ℹ️ <strong>{deleteWarning.summary.will_set_null}</strong> related record(s) will have fields set to <strong>NULL</strong>
                          </p>
                        )}
                        {!deleteWarning.can_delete && (
                          <p className="text-red-700 font-semibold">
                            🚫 Deletion is BLOCKED by protected relationships. You must handle dependencies first.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Affected Tables */}
                {deleteWarning.related_objects.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Table className="w-5 h-5" />
                      Affected Related Records
                    </h3>
                    <div className="space-y-2">
                      {deleteWarning.related_objects.map((rel, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            rel.severity === 'high' ? 'bg-red-50 border-red-300' :
                            rel.severity === 'medium' ? 'bg-yellow-50 border-yellow-300' :
                            'bg-blue-50 border-blue-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {rel.model}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Field: <code className="bg-white px-1 rounded">{rel.field}</code>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant={
                                rel.severity === 'high' ? 'destructive' :
                                rel.severity === 'medium' ? 'default' : 'secondary'
                              }>
                                {rel.count} record{rel.count !== 1 ? 's' : ''}
                              </Badge>
                              <div className="text-xs text-gray-600 mt-1">
                                {rel.on_delete}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delete Sequence */}
                {deleteWarning.delete_sequence && deleteWarning.delete_sequence.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Recommended Delete Sequence
                    </h3>
                    <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                      {deleteWarning.delete_sequence.map((step) => (
                        <div key={step.step} className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-semibold">
                            {step.step}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{step.action}</div>
                            <div className="text-sm text-gray-600">{step.reason}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warning Notice */}
                <div className="p-3 bg-gray-100 rounded-lg text-sm text-gray-700">
                  <strong>Note:</strong> This analysis shows the impact based on current database relationships. 
                  {deleteWarning.can_delete 
                    ? ' Proceeding will execute the deletion and all CASCADE/SET_NULL operations automatically.'
                    : ' You must manually resolve PROTECTED relationships before deletion is possible.'
                  }
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteWarning(false);
                  setPendingDeleteId(null);
                  setDeleteWarning(null);
                }}
              >
                Cancel
              </Button>
              {deleteWarning?.can_delete && (
                <Button 
                  variant="destructive"
                  onClick={() => {
                    if (deleteWarning.bulk_mode) {
                      executeBulkDelete();
                    } else {
                      executeDelete(pendingDeleteId);
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Confirm Delete
                  {deleteWarning.bulk_mode && ` (${deleteWarning.bulk_count})`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
